import { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles, CheckCircle2, XCircle, RotateCcw, FileDown } from "lucide-react";
import { toast } from "sonner";
import { API, api, fmtTime, withPersona, safe } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CanvasTabs } from "@/components/meridian/CanvasTabs";
import { ActivityLog } from "@/components/meridian/ActivityLog";
import { RuleBadge } from "@/components/meridian/Badges";

const JUR = [["UK", "United Kingdom"], ["EU", "European Union"], ["MX", "Mexico"]];
const SEV = { High: "text-red-700 border-red-500/50 bg-red-500/10", Medium: "text-amber-700 border-amber-500/40", Low: "text-slate-700 border-slate-300" };
const STATUS = { "Pending review": "text-amber-700 border-amber-500/40", Accepted: "text-emerald-700 border-emerald-500/50 bg-emerald-500/10", Rejected: "text-red-700 border-red-500/50", "Needs revision": "text-sky-700 border-sky-500/40" };

function RuleCell({ r }) {
  if (!r) return <td className="px-4 py-3 text-slate-400">-</td>;
  return (
    <td className="px-4 py-3 align-top">
      <div className="flex items-center gap-2"><RuleBadge id={r.id} /><span className="num text-[10px] text-slate-500">eff. {r.effective_date}</span></div>
      <div className="text-xs text-slate-900 mt-1">{r.title}</div>
      <div className="num text-[10px] text-indigo-700 mt-0.5">{r.citation}</div>
      <div className="text-[11px] text-slate-600 mt-1">{r.summary}</div>
    </td>
  );
}

function Comparison({ rules, j, topics }) {
  return (
    <div className="p-6">
      <div className="panel overflow-x-auto" data-testid="rule-comparison">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500"><th className="px-4 py-2 font-medium w-40">Topic</th><th className="px-4 py-2 font-medium">United States</th><th className="px-4 py-2 font-medium">{JUR.find((x) => x[0] === j)?.[1]}</th></tr></thead>
          <tbody>
            {topics.map((t) => (
              <tr key={t} className="border-t border-slate-200" data-testid={`comparison-row-${t.split(" ")[0].toLowerCase()}`}>
                <td className="px-4 py-3 align-top text-slate-800">{t}</td>
                <RuleCell r={rules.find((r) => r.jurisdiction === "US" && r.topic === t)} />
                <RuleCell r={rules.find((r) => r.jurisdiction === j && r.topic === t)} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GapCard({ g, onReview }) {
  const [comment, setComment] = useState("");
  const { personaLabel: reviewer } = useApp();
  return (
    <div className="panel p-4 rise" data-testid={`gap-card-${g.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${SEV[g.severity] || SEV.Medium}`}>{g.severity}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-700">{g.direction}</span>
        <span className="text-[11px] text-slate-500">{g.topic}</span>
        <span data-testid={`gap-status-${g.id}`} className={`ml-auto text-[10px] px-1.5 py-0.5 rounded border ${STATUS[g.review_status]}`}>{g.review_status}</span>
      </div>
      <div className="text-sm text-slate-900 font-medium mt-2">{g.title}</div>
      <p className="text-xs text-slate-700 mt-1">{g.description}</p>
      <div className="grid md:grid-cols-2 gap-2 mt-2 text-[11px]">
        <div className="text-slate-600"><span className="text-slate-500">Impact: </span>{g.impact}</div>
        <div className="text-slate-600"><span className="text-slate-500">Recommended action: </span>{g.recommended_action}</div>
      </div>
      <div className="flex items-center gap-2 mt-3"><span className="text-[10px] text-slate-500">Citations</span><RuleBadge id={g.us_rule_id} /><RuleBadge id={g.foreign_rule_id} /><span className="ml-auto num text-[10px] text-slate-400">AI draft · {g.model} · {g.run_id} · {fmtTime(g.generated_at)}</span></div>
      {g.reviews?.length > 0 && <div className="mt-2 space-y-0.5">{g.reviews.map((r, i) => <div key={i} className="text-[11px] text-slate-600">{r.status} by {r.reviewer} · {fmtTime(r.at)}{r.comment ? ` - "${r.comment}"` : ""}</div>)}</div>}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-200">
        <span data-testid={`gap-reviewer-${g.id}`} className="text-[11px] text-slate-500 self-center">Signing as {reviewer}</span>
        <input data-testid={`gap-comment-${g.id}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment (optional)" className="flex-1 min-w-[140px] bg-slate-50 border border-slate-300 rounded px-2 py-1 text-[11px] outline-none" />
        <button data-testid={`gap-accept-${g.id}`} onClick={() => onReview(g.id, "Accepted", reviewer, comment)} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10"><CheckCircle2 className="w-3 h-3" />Accept</button>
        <button data-testid={`gap-revise-${g.id}`} onClick={() => onReview(g.id, "Needs revision", reviewer, comment)} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 text-sky-700 hover:bg-sky-600/10"><RotateCcw className="w-3 h-3" />Needs revision</button>
        <button data-testid={`gap-reject-${g.id}`} onClick={() => onReview(g.id, "Rejected", reviewer, comment)} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border border-red-500/40 text-red-700 hover:bg-red-500/10"><XCircle className="w-3 h-3" />Reject</button>
      </div>
    </div>
  );
}

export default function ComplianceCanvas({ task, tab, setTab, reload, version }) {
  const j = task.jurisdiction || "UK";
  const [rules, setRules] = useState([]);
  const [topics, setTopics] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadGaps = useCallback(() => api.get(`/compliance/gaps?jurisdiction=${j}`).then((r) => setGaps(r.data)), [j]);
  useEffect(() => { api.get(`/compliance/rules?jurisdiction=${j}`).then((r) => setRules(r.data)); loadGaps(); }, [j, loadGaps, version]);
  useEffect(() => { api.get("/compliance/meta").then((r) => setTopics(r.data.topics)); }, []);
  const setJ = safe(async (x) => { await api.put(`/tasks/${task.id}/jurisdiction`, { jurisdiction: x }); reload(); });
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/compliance/analyze", { jurisdiction: j }); await loadGaps(); reload(); setTab("gaps"); toast.success(`${data.count} gaps drafted - pending human review`); }
    catch (e) { if (e.response?.status !== 403) toast.error("Gap analysis failed - retry"); }
    setBusy(false);
  };
  const review = async (id, status, reviewer, comment) => {
    try { await api.put(`/compliance/gaps/${id}/review`, { status, reviewer, comment }); await loadGaps(); reload(); toast.success(`Gap marked ${status}`); }
    catch (e) { if (e.response?.status !== 403) toast.error("Review failed"); }
  };
  const counts = gaps.reduce((a, g) => ({ ...a, [g.review_status]: (a[g.review_status] || 0) + 1 }), {});
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="compliance-canvas">
      <div className="px-6 py-4 border-b border-slate-200 space-y-3">
        <div className="flex flex-wrap justify-between items-end gap-3">
          <div><div className="eyebrow">Cross-Border Compliance · Regulatory rule library</div><h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">US vs {JUR.find((x) => x[0] === j)?.[1]}</h1></div>
          <div className="flex gap-2">
          <a data-testid="gap-report-export" href={withPersona(`${API}/compliance/report.pdf?jurisdiction=${j}`)} target="_blank" rel="noreferrer" onClick={(e) => { if (!counts.Accepted) { e.preventDefault(); toast.error("Accept at least one gap to export the report"); } }} className="flex items-center gap-1.5 border border-sky-500/50 text-sky-700 hover:bg-sky-600/10 text-xs px-3 py-2 rounded transition-colors"><FileDown className="w-3.5 h-3.5" />Gap report PDF ({counts.Accepted || 0} accepted)</a>
          <button data-testid="run-gap-analysis" disabled={busy} onClick={run} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium px-3 py-2 rounded transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{busy ? "Drafting gaps…" : "Run AI gap analysis"}
          </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow">Jurisdiction</span>
          {JUR.map(([k, l]) => <button key={k} data-testid={`jurisdiction-${k}`} onClick={() => setJ(k)} className={`text-xs px-3 py-1 rounded border transition-colors ${j === k ? "border-sky-500/60 bg-sky-600/15 text-sky-900" : "border-slate-300 text-slate-600 hover:text-slate-900"}`}>{l}</button>)}
          <span className="ml-auto text-[11px] num text-slate-500" data-testid="gap-review-summary">{gaps.length} gaps · {counts["Pending review"] || 0} pending · {counts.Accepted || 0} accepted · {counts.Rejected || 0} rejected · {counts["Needs revision"] || 0} revision</span>
        </div>
      </div>
      <CanvasTabs tab={tab} setTab={setTab} tabs={[["dashboard", "Rule comparison"], ["gaps", "Gap analysis", gaps.length], ["activity", "Activity"]]} />
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll">
        {tab === "dashboard" && <Comparison rules={rules} j={j} topics={topics} />}
        {tab === "gaps" && (
          <div className="p-6 space-y-3" data-testid="gap-list">
            <p className="text-[11px] text-amber-700">AI-drafted gaps stay "Pending review" until a compliance reviewer signs off. Rule summaries reference the official texts cited.</p>
            {!gaps.length && <div className="panel p-8 text-sm text-slate-500">No gaps yet for this jurisdiction. Run the AI gap analysis.</div>}
            {gaps.map((g) => <GapCard key={g.id} g={g} onReview={review} />)}
          </div>
        )}
        {tab === "activity" && <ActivityLog taskId={task.id} version={version} />}
      </div>
    </div>
  );
}
